import React, { useEffect, useState, useRef } from "react";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import PrintIcon from "@mui/icons-material/Print";
import baseUrl from "../../../../api/baseUrl";
import { useDispatch, useSelector } from "react-redux";
import {
  addTableOrderItem,
  increaseTableOrderItemQty,
  decreaseTableOrderItemQty,
  removeTableOrderItem,
  updateTableOrderDiscount,
  setTableOrderStatus,
  setTable,
  clearTableOrder,
  addKotPrintTime,
  saveCurrentTableOrder,
  setPaidAmount,
} from "../../../../store/tableOrder/tableOrderSlice";
import { RootState } from "../../../../store/store";
import ReceiptNew from "../../../../views/print/ReceiptNew";
import "./../../../../views/print/receipt.scss";
import { getDecryptedToken } from "../../../../utils/crypto/cryptoUtils";
import { getCurrencySymbol } from "../../../../utils/currency/currencyUtils";
import { toast } from "react-toastify";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { useNavigate } from "react-router-dom";
import useSubmitting from "../../../../hooks/useSubmitting";

interface TablePopupProps {
  tableNumber: string;
  peopleCount: number;
  tableId?: number;
  onClose: () => void;
}

const statusOptions = ["Available", "Ordered", "Ongoing", "Reserved"];

const TablePopup: React.FC<TablePopupProps> = ({
  tableNumber,
  peopleCount,
  tableId,
  onClose,
}) => {
  const dispatch = useDispatch();
  const tableOrder = useSelector(
    (state: RootState) => state.tableOrder.current,
  );
  const navigate = useNavigate();

  // Fetch items for add-item dropdown
  const [items, setItems] = useState<any[]>([]);
  const [addForm, setAddForm] = useState({ itemId: "", qty: 1, discount: 0 });
  const [showPayment, setShowPayment] = useState(false);
  const paid = tableOrder.paid || 0;
  const [showPrintPopup, setShowPrintPopup] = useState(false);
  const [showFinalPrintPopup, setShowFinalPrintPopup] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);
  const finalPrintRef = useRef<HTMLDivElement>(null);

  // KOT-style order details from API
  const [orderDetails, setOrderDetails] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [tableStatus, setTableStatus] = useState<string>("Available");
  const [statusLoading, setStatusLoading] = useState(false);
  const [billOrderNumber, setBillOrderNumber] = useState<string>("");

  useEffect(() => {
    baseUrl.get("/branch/pos/find-items").then((res) => {
      setItems(res.data.data || []);
    });
    // Set table info in redux
    dispatch(setTable({ tableNumber, peopleCount, tableId }));
    // eslint-disable-next-line
  }, [tableNumber, peopleCount, tableId]);

  // Fetch order details and table status for this table from API (like KOT)
  useEffect(() => {
    if (!tableId) {
      setOrderDetails([]);
      setTableStatus("Available");
      setBillOrderNumber("");
      return;
    }
    setLoading(true);
    baseUrl
      .get(`/branch/order/table/${tableId}`)
      .then((res) => {
        const orders = res.data.data || [];
        setOrderDetails(orders);
        setBillOrderNumber(orders[0]?.orderNumber || "");
      })
      .catch((err) => {
        console.error("Error fetching order details:", err);
        setOrderDetails([]);
        setBillOrderNumber("");
      })
      .finally(() => setLoading(false));
    // Fetch table status from API
    baseUrl
      .get(`/branch/table/get-table/${tableId}`)
      .then((res) => setTableStatus(res.data.data?.status || "Available"))
      .catch(() => setTableStatus("Available"));
  }, [tableId]);

  const handleAddItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addForm.itemId || !billOrderNumber) return;
    const item = items.find((i) => i.id === Number(addForm.itemId));
    if (!item) return;
    try {
      await baseUrl.post("/branch/order/create-order", {
        orders: [
          {
            itemId: item.id,
            quantity: addForm.qty,
            orderType: "table",
            status: "Started",
          },
        ],
        tableId,
        peopleCount,
        waiterId: 1,
        orderNumber: billOrderNumber,
      });
      // Refresh order details
      baseUrl
        .get(`/branch/order/table/${tableId}`)
        .then((res) => setOrderDetails(res.data.data || []));
      setAddForm({ itemId: "", qty: 1, discount: 0 });
    } catch (err) {
      console.error("Error adding item:", err);
    }
  };
  const [submitting, wrap] = useSubmitting();

  // Delete item from order if status is "Started"
  const handleDeleteOrderItem = async (orderId: number) => {
    try {
      await baseUrl.put(`/branch/order/item/${orderId}/status`, {
        status: "Cancelled",
      });
      setOrderDetails((prev) =>
        prev.map((o: any) =>
          o.id === orderId ? { ...o, status: "Cancelled" } : o,
        ),
      );
    } catch (err) {
      console.error("Error deleting order item:", err);
    }
  };

  // Calculate totals for table bill (use orderDetails, not tableOrder.orders)
  const tableBillTotal = orderDetails.reduce(
    (sum: number, order: any) =>
      sum + Number(order.price || 0) * Number(order.quantity || 0),
    0,
  );

  // Print normal bill (show print popup, then print)
  const handlePrint = () => {
    setShowPrintPopup(true);
  };

  // Print final bill (show final print popup, then print)
  const handleFinishBill = async () => {
    if (!billOrderNumber) return;
    setShowPayment(true);
    // Optionally, you can update all items to "Completed" here after payment
    // Or do it in handleFinalPrintConfirm after payment is done
  };

  // Actually print when user confirms in popup
  const handlePrintConfirm = async () => {
    if (printRef.current) {
      try {
        printRef.current.style.display = "block";
        // Use window.print() on a hidden iframe for direct print, no new tab
        const printContents = printRef.current.innerHTML;
        const printFrame = document.createElement("iframe");
        printFrame.style.position = "fixed";
        printFrame.style.right = "0";
        printFrame.style.bottom = "0";
        printFrame.style.width = "0";
        printFrame.style.height = "0";
        printFrame.style.border = "0";
        document.body.appendChild(printFrame);

        printFrame.contentDocument!.open();
        printFrame.contentDocument!.write(`
          <html>
            <head>
              <title>Print Bill</title>
              <link rel="stylesheet" href="/src/views/print/receipt.scss" />
              <style>
                body { background: #fff; margin: 0; }
              </style>
            </head>
            <body>${printContents}</body>
          </html>
        `);
        printFrame.contentDocument!.close();

        printFrame.onload = () => {
          printFrame.contentWindow!.focus();
          printFrame.contentWindow!.print();
          setTimeout(() => {
            document.body.removeChild(printFrame);
          }, 1000);
        };
      } catch (err) {}
    }
    setShowPrintPopup(false);
    dispatch(addKotPrintTime());
    dispatch(saveCurrentTableOrder());
  };

  //   const handleFinalPrintConfirm = async () => {
  //     if (finalPrintRef.current) {
  //       try {
  //         finalPrintRef.current.style.display = "block";
  //         const element = finalPrintRef.current;
  //         const html2canvas = (await import("html2canvas")).default;
  //         const jsPDF = (await import("jspdf")).jsPDF;
  //         const options = {
  //           scale: 2,
  //           useCORS: true,
  //           allowTaint: true,
  //         };
  //         const canvas = await html2canvas(element, options);
  //         const imgData = canvas.toDataURL("image/png");
  //         const imgWidth = 80;
  //         const imgProps = canvas.width / canvas.height;
  //         const imgHeight = imgWidth / imgProps;
  //         const pdf = new jsPDF({
  //           orientation: "p",
  //           unit: "mm",
  //           format: [imgWidth, imgHeight],
  //         });
  //         pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight, "", "FAST");
  //         pdf.autoPrint && pdf.autoPrint();
  //         const pdfBlob = pdf.output("blob");
  //         const blobUrl = URL.createObjectURL(pdfBlob);

  //         const printWindow = window.open(blobUrl, "_blank");
  //         if (printWindow) {
  //           printWindow.onload = () => {
  //             printWindow.focus();
  //             printWindow.print();
  //           };
  //         }
  //       } catch (err) {}
  //     }
  //     setShowFinalPrintPopup(false);
  //     dispatch(addKotPrintTime());
  //     dispatch(saveCurrentTableOrder());
  //     dispatch(clearTableOrder());
  //     onClose();
  //   };

  const handlePay = () => {
    setShowPayment(false);
    // dispatch(setPaidAmount(0));
    setShowFinalPrintPopup(true);
  };

  // Add shop/company details for receipt header
  const shopName = getDecryptedToken("martName");
  const address = getDecryptedToken("address");
  const phoneNumber = getDecryptedToken("phoneNumber");
  const name = getDecryptedToken("name");
  const email = getDecryptedToken("email");
  const currency = getDecryptedToken("currency");
  const currencySymbol = getCurrencySymbol();

  // Add state for loading and error
  const [isPrinting, setIsPrinting] = useState(false);

  // Helper: Prepare sale payload for API
  const getSalePayload = () => {
    const drawerId = getDecryptedToken("selectedDrawer");
    return {
      productData: tableOrder.orders.map((item: any) => ({
        itemId: item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        discount: item.discount,
        priceHistoryId: item.priceHistoryId,
        ...item,
      })),
      card: 0,
      cash: paid,
      bank: 0,
      balance: paid - tableBillTotal,
      discount: 0,
      givenDiscount: 0,
      tax: 0,
      total: tableBillTotal,
      netTotal: tableBillTotal,
      drawerId: Number(drawerId),
      customerId: null,
      saleType: "cash",
      creditSale: false,
      // You may add tableId or tableNumber if needed for backend
      tableId: tableOrder.tableId,
      tableNumber: tableOrder.tableNumber,
    };
  };

  // Print and complete sale (API call, print, clear, close)
  const handleFinalPrintConfirm = async () => {
    if (isPrinting) return;
    setIsPrinting(true);
    const loadingToastId = toast.loading("Processing sale...", {
      position: "top-center",
    });
    try {
      // 1. Call API (same as POS)
      const payload = getSalePayload();
      const res = await baseUrl.post("/branch/pos/create-sale", payload);

      // 2. Print receipt (same as POS)
      if (finalPrintRef.current) {
        finalPrintRef.current.style.display = "block";
        const element = finalPrintRef.current;
        const options = {
          scale: 2,
          useCORS: true,
          allowTaint: true,
        };
        const canvas = await html2canvas(element, options);
        const imgData = canvas.toDataURL("image/png");
        const imgWidth = 80;
        const imgProps = canvas.width / canvas.height;
        const imgHeight = imgWidth / imgProps;
        const pdf = new jsPDF({
          orientation: "p",
          unit: "mm",
          format: [imgWidth, imgHeight],
        });
        pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight, "", "FAST");
        pdf.autoPrint && pdf.autoPrint();
        const pdfBlob = pdf.output("blob");
        const blobUrl = URL.createObjectURL(pdfBlob);

        const printWindow = window.open(blobUrl, "_blank");
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.focus();
            printWindow.print();
          };
        }
      }

      // 3. Mark all items as Completed (close bill)
      if (billOrderNumber) {
        await baseUrl.put(
          `/branch/order/order-number/${billOrderNumber}/status`,
          { status: "Completed" },
        );
      }

      // 4. Success toast
      toast.dismiss(loadingToastId);
      toast.success("Table bill completed successfully!", {
        position: "top-center",
        autoClose: 3000,
      });

      // 5. Clear order, close popup
      setShowFinalPrintPopup(false);
      dispatch(addKotPrintTime());
      dispatch(saveCurrentTableOrder());
      dispatch(clearTableOrder());
      onClose();
    } catch (error: any) {
      toast.dismiss(loadingToastId);
      // Error handling (same as POS)
      const errorMessage = error.response?.data?.message;
      if (errorMessage) {
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 5000,
        });
      } else {
        toast.error("Failed to process sale. Please try again.", {
          position: "top-center",
          autoClose: 5000,
        });
      }
      // Do not clear/close/print
    } finally {
      setIsPrinting(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!billOrderNumber) return;

    const loadingToastId = toast.loading("Cancelling order...", {
      position: "top-center",
    });

    try {
      await baseUrl.put(`/branch/order/cancel-order/${billOrderNumber}`);

      toast.dismiss(loadingToastId);
      toast.success("Order cancelled successfully!", {
        position: "top-center",
        autoClose: 3000,
      });

      setOrderDetails([]);
      setBillOrderNumber("");
      setTableStatus("Available");
      onClose();
    } catch (error: any) {
      toast.dismiss(loadingToastId);
      const errorMessage = error.response?.data?.message;
      if (errorMessage) {
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 5000,
        });
      } else {
        toast.error("Failed to cancel order. Please try again.", {
          position: "top-center",
          autoClose: 5000,
        });
      }
    }
  };

  // Send KOT handler: save all table orders to redux, then navigate to KOT
  const handleSendKot = () => {
    // Save current table order to redux (if not already)
    dispatch(saveCurrentTableOrder());
    // Navigate to KOT page (no state needed, KOT reads from redux)
    navigate("/kot");
  };

  // (Removed duplicate state declarations and useEffect for order details and table status)

  // Update table status via API
  const handleStatusChange = async (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const newStatus = e.target.value;
    setStatusLoading(true);
    try {
      await baseUrl.patch(`/branch/table/update-table-status/${tableId}`, {
        status: newStatus,
      });
      setTableStatus(newStatus);
      dispatch(setTableOrderStatus(newStatus));
    } catch {
      // fallback: revert to previous status
      dispatch(setTableOrderStatus(tableStatus));
    }
    setStatusLoading(false);
  };

  const orderStatusToneClass = (status: string) => {
    switch (status) {
      case "Started":
      case "Preparing":
        return "bg-erp-gold";
      case "Ready":
        return "bg-erp-success";
      case "Served":
        return "bg-emerald-500";
      case "Completed":
        return "bg-indigo-500";
      case "Cancelled":
        return "bg-erp-danger";
      default:
        return "bg-slate-400";
    }
  };

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-erp-navy/35 p-4 backdrop-blur-[2px]">
      <div className="flex max-h-[95vh] w-full max-w-[760px] flex-col overflow-y-auto rounded-2xl bg-white p-6 font-inter shadow-[0_8px_32px_rgba(11,28,44,0.18)]">
        <div className="mb-2 flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <h3 className="m-0 text-[22px] font-bold text-erp-ink">
              Table {tableOrder.tableNumber}
              <span className="ml-2 text-[14px] font-normal text-erp-muted">
                ({tableOrder.peopleCount} People)
              </span>
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-3 inline-flex h-8 w-8 items-center justify-center rounded-full text-erp-ink transition-colors hover:bg-erp-surface"
            aria-label="Close"
            title="Close"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="mb-3 flex items-center gap-2">
          <label className="text-[13px] font-semibold text-erp-ink">
            Status:
          </label>
          <select
            value={tableStatus}
            onChange={handleStatusChange}
            disabled={statusLoading}
            className="h-8 rounded-md border border-erp-line bg-erp-surface/60 px-2.5 text-[13px] text-erp-ink outline-none transition-colors focus:border-erp-accent focus:ring-2 focus:ring-erp-accent/15"
          >
            {statusOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          {statusLoading && (
            <span className="text-[12px] text-erp-muted">Updating...</span>
          )}
        </div>

        {/* KOT-style order details */}
        <div className="mb-4">
          <h4 className="m-0 mb-2 text-[13px] font-semibold text-erp-ink">
            Current Orders
          </h4>
          {loading ? (
            <div className="text-[12px] text-erp-muted">Loading...</div>
          ) : orderDetails.length === 0 ? (
            <div className="text-[12px] text-erp-muted">
              No order details found.
            </div>
          ) : (
            <div className="max-h-[320px] w-full overflow-y-auto overflow-x-auto rounded-md border border-erp-line">
              <table className="w-full min-w-[560px] border-collapse text-left">
                <thead className="bg-erp-surface/70">
                  <tr className="border-b border-erp-line text-[10px] font-semibold uppercase tracking-[0.06em] text-erp-muted">
                    <th className="px-3 py-2">Order #</th>
                    <th className="px-3 py-2">Item</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="px-3 py-2 text-right">Price</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {orderDetails.map((order: any, idx: number) => (
                    <tr
                      key={order.id || idx}
                      className="border-b border-erp-line/60 last:border-b-0 even:bg-erp-surface/25"
                    >
                      <td className="whitespace-nowrap px-3 py-2 text-[12px] text-erp-ink">
                        {order.orderNumber}
                      </td>
                      <td className="px-3 py-2 text-[12px] text-erp-ink">
                        {order.itemName || order.name}
                        <br />
                        <span className="text-[11px] text-erp-muted">
                          {order.itemNumber}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right text-[12px] tabular-nums text-erp-ink">
                        {order.quantity}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right text-[12px] tabular-nums text-erp-ink">
                        {order.price ? Number(order.price).toFixed(2) : "-"}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right text-[12px] font-semibold tabular-nums text-erp-ink">
                        {order.price && order.quantity
                          ? (
                              Number(order.price) * Number(order.quantity)
                            ).toFixed(2)
                          : "-"}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2">
                        <span
                          className={`inline-flex rounded px-2 py-0.5 text-[11px] font-semibold text-white ${orderStatusToneClass(order.status)}`}
                        >
                          {order.status}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2">
                        {order.status === "Started" && (
                          <button
                            type="button"
                            className="inline-flex h-7 items-center rounded-md border border-erp-danger/25 bg-erp-danger/10 px-2 text-[11px] font-semibold text-erp-danger transition-colors hover:border-erp-danger/45 hover:bg-erp-danger/20"
                            onClick={() => handleDeleteOrderItem(order.id)}
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {/* Show total for all items in this bill */}
                <tfoot>
                  <tr className="border-t border-erp-line bg-erp-surface/40">
                    <td
                      colSpan={4}
                      className="whitespace-nowrap px-3 py-2 text-right text-[12px] font-semibold text-erp-ink"
                    >
                      Total:
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right text-[12px] font-semibold tabular-nums text-erp-ink">
                      {orderDetails
                        .reduce(
                          (sum: number, order: any) =>
                            sum +
                            Number(order.price || 0) *
                              Number(order.quantity || 0),
                          0,
                        )
                        .toFixed(2)}
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Add Item Section (only if bill is open) */}
        {billOrderNumber && (
          <form onSubmit={wrap(handleAddItem)} className="mb-1">
            <div className="flex items-center gap-2">
              <select
                name="itemId"
                value={addForm.itemId}
                onChange={(e) =>
                  setAddForm((f) => ({ ...f, itemId: e.target.value }))
                }
                required
                className="h-9 flex-[2] rounded-md border border-erp-line bg-erp-surface/60 px-2.5 text-[13px] text-erp-ink outline-none transition-colors focus:border-erp-accent focus:ring-2 focus:ring-erp-accent/15"
              >
                <option value="">Select Item</option>
                {items.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name} {item.itemNumber ? `(${item.itemNumber})` : ""}
                  </option>
                ))}
              </select>
              <input
                type="number"
                name="qty"
                value={addForm.qty}
                min={1}
                onChange={(e) =>
                  setAddForm((f) => ({ ...f, qty: Number(e.target.value) }))
                }
                className="h-9 w-16 rounded-md border border-erp-line bg-erp-surface/60 px-2 text-center text-[13px] text-erp-ink outline-none transition-colors focus:border-erp-accent focus:ring-2 focus:ring-erp-accent/15"
                required
              />
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex h-9 items-center justify-center rounded-md border-0 bg-[linear-gradient(90deg,#0b1c2c,#123048)] px-3.5 text-[13px] font-semibold text-white shadow-[0_2px_6px_rgba(11,28,44,0.16)] transition-[filter] hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? "Adding..." : "Add"}
              </button>
            </div>
          </form>
        )}

        {/* Actions */}
        <div className="mt-4 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={handleSendKot}
            className="h-10 min-w-[140px] flex-1 rounded-md bg-indigo-600 text-[14px] font-semibold text-white transition-colors hover:bg-indigo-700"
          >
            Send to KOT
          </button>
          <button
            type="button"
            onClick={handlePrint}
            className="h-10 min-w-[140px] flex-1 rounded-md bg-erp-gold text-[14px] font-semibold text-white transition-colors hover:brightness-95"
          >
            Print Bill
          </button>
          <button
            type="button"
            onClick={handleFinishBill}
            className="h-10 min-w-[140px] flex-1 rounded-md bg-erp-success text-[14px] font-semibold text-white transition-colors hover:brightness-95"
          >
            Finish Bill
          </button>
          <button
            type="button"
            onClick={handleCancelOrder}
            className="h-10 min-w-[140px] flex-1 rounded-md bg-erp-danger text-[14px] font-semibold text-white transition-colors hover:brightness-95"
          >
            Cancel Order
          </button>
        </div>

        {/* Totals */}
        <div className="mt-4 font-semibold">
          <div className="mb-2 flex items-center justify-between text-[16px]">
            <span className="text-erp-ink">Total:</span>
            <span className="font-bold text-erp-gold">
              {tableBillTotal.toFixed(2)}
            </span>
          </div>
          {showPayment && (
            <div className="mt-2.5 rounded-md border border-erp-line bg-erp-surface/40 p-3">
              <div className="flex items-center gap-2">
                <label className="text-[13px] font-medium text-erp-ink">
                  Paid:
                </label>
                <input
                  type="number"
                  value={paid}
                  min={0}
                  onChange={(e) =>
                    dispatch(setPaidAmount(Number(e.target.value)))
                  }
                  className="h-8 w-24 rounded-md border border-erp-line bg-white px-2 text-[13px] text-erp-ink outline-none transition-colors focus:border-erp-accent focus:ring-2 focus:ring-erp-accent/15"
                />
              </div>
              <div className="mt-2 text-[13px] text-erp-ink">
                Balance: {(paid - tableBillTotal).toFixed(2)}
              </div>
              <button
                type="button"
                onClick={handlePay}
                className="mt-2.5 h-9 rounded-md bg-erp-success px-4 text-[13px] font-semibold text-white transition-colors hover:brightness-95"
              >
                Pay &amp; Finish
              </button>
            </div>
          )}
        </div>
        {/* Print Bill Popup (Receipt style) */}
        {showPrintPopup && (
          <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-erp-navy/40 p-4">
            <div className="flex max-h-[90vh] max-w-[95vw] min-w-[350px] flex-col items-center overflow-auto rounded-xl bg-white p-6 shadow-[0_4px_24px_rgba(0,0,0,0.18)]">
              {/* Receipt-style print for table bill */}
              <div ref={printRef}>
                <div className="receipt" style={{ background: "white" }}>
                  <div className="headers">
                    <div className="title">{shopName}</div>
                    <div className="address">{address}</div>
                    <div className="tell">{phoneNumber}</div>
                    <div className="email">{email}</div>
                  </div>
                  <div
                    style={{
                      textAlign: "center",
                      fontWeight: 700,
                      fontSize: 18,
                      marginBottom: 8,
                    }}
                  >
                    TABLE BILL
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <strong>TABLE:</strong> {tableOrder.tableNumber} &nbsp;
                    <strong>PEOPLE:</strong> {tableOrder.peopleCount}
                  </div>
                  <hr />
                  <div className="heading">
                    <div className="row1">
                      <span>LN</span>
                      <span>ITEMS</span>
                    </div>
                    <div className="row2">
                      <span>QTY</span>
                      <span>RATE</span>
                      <span>GROSS</span>
                      <span>DIS.</span>
                      <span>NET AMT</span>
                    </div>
                  </div>
                  <hr />
                  {orderDetails.map((item: any, i: number) => (
                    <div className="r_items" key={i}>
                      <div className="row1">
                        <span>{String(i + 1).padStart(2, "0")}.</span>
                        <span>{item?.itemName || item?.name}</span>
                      </div>
                      <div className="row2">
                        <span>{item?.quantity}</span>
                        <span>
                          {currencySymbol} {Number(item?.price).toFixed(2)}
                        </span>
                        <span>X</span>
                        <span>0</span>
                        <span>
                          {currencySymbol}{" "}
                          {(
                            Number(item?.price) * Number(item?.quantity)
                          ).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ))}
                  <hr />
                  <div
                    style={{
                      textAlign: "right",
                      fontWeight: 600,
                      marginTop: 8,
                    }}
                  >
                    TOTAL: {currencySymbol}
                    {tableBillTotal.toFixed(2)}
                  </div>
                  <div className="thank" style={{ marginTop: 12 }}>
                    THANK YOU! COME AGAIN
                  </div>
                </div>
              </div>
              <div className="mt-4 flex gap-4">
                <button
                  type="button"
                  onClick={handlePrintConfirm}
                  className="rounded-md bg-indigo-600 px-6 py-2.5 text-[16px] font-semibold text-white transition-colors hover:bg-indigo-700"
                >
                  Print
                </button>
                <button
                  type="button"
                  onClick={() => setShowPrintPopup(false)}
                  className="rounded-md bg-slate-300 px-6 py-2.5 text-[16px] font-semibold text-slate-700 transition-colors hover:bg-slate-400"
                >
                  Cancel
                </button>
              </div>
              {/* Add button to open final bill popup */}
              <div className="mt-3 w-full">
                <button
                  type="button"
                  onClick={() => setShowFinalPrintPopup(true)}
                  className="w-full rounded-md bg-erp-success px-6 py-2.5 text-[16px] font-semibold text-white transition-colors hover:brightness-95"
                >
                  Print Final Bill
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Print Final Bill Popup (Receipt style) */}
        {showFinalPrintPopup && (
          <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-erp-navy/40 p-4">
            <div className="flex max-h-[90vh] max-w-[95vw] min-w-[350px] flex-col items-center overflow-auto rounded-xl bg-white p-6 shadow-[0_4px_24px_rgba(0,0,0,0.18)]">
              <div ref={finalPrintRef}>
                <div className="receipt" style={{ background: "white" }}>
                  <div className="headers">
                    <div className="title">{shopName}</div>
                    <div className="address">{address}</div>
                    <div className="tell">{phoneNumber}</div>
                    <div className="email">{email}</div>
                  </div>
                  <div
                    style={{
                      textAlign: "center",
                      fontWeight: 700,
                      fontSize: 18,
                      marginBottom: 8,
                    }}
                  >
                    FINAL TABLE BILL
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <strong>TABLE:</strong> {tableOrder.tableNumber} &nbsp;
                    <strong>PEOPLE:</strong> {tableOrder.peopleCount}
                  </div>
                  <hr />
                  <div className="heading">
                    <div className="row1">
                      <span>LN</span>
                      <span>ITEMS</span>
                    </div>
                    <div className="row2">
                      <span>QTY</span>
                      <span>RATE</span>
                      <span>GROSS</span>
                      <span>DIS.</span>
                      <span>NET AMT</span>
                    </div>
                  </div>
                  <hr />
                  {/* Use orderDetails for final bill */}
                  {orderDetails?.map((item: any, i: number) => (
                    <div className="r_items" key={i}>
                      <div className="row1">
                        <span>{String(i + 1).padStart(2, "0")}.</span>
                        <span>{item?.itemName || item?.name}</span>
                      </div>
                      <div className="row2">
                        <span>{item?.quantity}</span>
                        <span>
                          {currencySymbol} {Number(item?.price).toFixed(2)}
                        </span>
                        <span>
                          {currencySymbol}{" "}
                          {(
                            Number(item?.price) * Number(item?.quantity)
                          ).toFixed(2)}
                        </span>
                        <span>{item?.discount || 0}</span>
                        <span>
                          {currencySymbol}{" "}
                          {(
                            Number(item?.price) * Number(item?.quantity) -
                            parseFloat(item?.discount || 0) *
                              Number(item?.quantity)
                          ).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ))}
                  <hr />
                  {/* Payment details section */}
                  <div className="amounts" style={{ marginTop: 8 }}>
                    <div className="datas">
                      <span>Total</span>
                      <span>
                        {currencySymbol}
                        {orderDetails
                          .reduce(
                            (sum: number, item: any) =>
                              sum +
                              (Number(item.price) * Number(item.quantity) -
                                parseFloat(item.discount || 0) *
                                  Number(item.quantity)),
                            0,
                          )
                          .toFixed(2)}
                      </span>
                    </div>
                    {/* Final Discount */}
                    <div className="datas">
                      <span>Final Discount</span>
                      <span>
                        {currencySymbol}{" "}
                        {(
                          orderDetails.reduce(
                            (sum: number, item: any) =>
                              sum +
                              parseFloat(item.discount || 0) *
                                Number(item.quantity),
                            0,
                          ) || 0
                        ).toFixed(2)}
                      </span>
                    </div>
                    {/* Payment breakdown */}
                    <div className="datas">
                      <span>Cash</span>
                      <span>
                        {currencySymbol}{" "}
                        {(tableOrder.cash || tableOrder.paid || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="datas">
                      <span>Card</span>
                      <span>
                        {currencySymbol} {(tableOrder.card || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="datas">
                      <span>Bank</span>
                      <span>
                        {currencySymbol} {(tableOrder.bank || 0).toFixed(2)}
                      </span>
                    </div>
                    {/* Paid and Balance */}
                    <div className="datas">
                      <span>Paid</span>
                      <span>
                        {currencySymbol} {(paid || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="datas">
                      <span>Balance</span>
                      <span>
                        {currencySymbol}
                        {(
                          (paid || 0) -
                          orderDetails.reduce(
                            (sum: number, item: any) =>
                              sum +
                              (Number(item.price) * Number(item.quantity) -
                                parseFloat(item.discount || 0) *
                                  Number(item.quantity)),
                            0,
                          )
                        ).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <div className="thank" style={{ marginTop: 12 }}>
                    THANK YOU! COME AGAIN
                  </div>
                </div>
              </div>
              <div className="mt-4 flex gap-4">
                <button
                  type="button"
                  onClick={handleFinalPrintConfirm}
                  className="rounded-md bg-indigo-600 px-6 py-2.5 text-[16px] font-semibold text-white transition-colors hover:bg-indigo-700"
                >
                  Print
                </button>
                <button
                  type="button"
                  onClick={() => setShowFinalPrintPopup(false)}
                  className="rounded-md bg-slate-300 px-6 py-2.5 text-[16px] font-semibold text-slate-700 transition-colors hover:bg-slate-400"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TablePopup;
